import React from "react";
import { createPortal } from "react-dom";
import { X, TrendingUp, Target, Shield, Star, DollarSign } from "lucide-react";
import { useModelInfoModal, getRiskManagement, getMarketConditions, type Model } from "./useModelInfoModal";

type ModelInfoModalProps = {
  isOpen: boolean;
  onClose: () => void;
  model: Model | null;
};

const ModelInfoModal: React.FC<ModelInfoModalProps> = ({ isOpen, onClose, model }) => {
  const { formatPercentage, formatCurrency, formatNumber, getModelIcon, getModelIconColor, getRiskLevelColor } =
    useModelInfoModal();

  if (!isOpen || !model) return null;

  const ModelIcon = getModelIcon(model.type);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/75" onClick={onClose} aria-hidden="true"></div>

      <div className="relative flex max-h-full w-full max-w-4xl flex-col overflow-hidden rounded-lg border border-white/10 bg-ink-900 text-left shadow-xl">
        <div className="overflow-y-auto px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <ModelIcon className={`h-6 w-6 ${getModelIconColor(model.type)}`} />
                <div className="ml-3">
                  <h3 className="text-2xl font-bold text-slate-100">{model.name}</h3>
                </div>
              </div>
              <button onClick={onClose} className="text-slate-500 hover:text-slate-300 transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-slate-300 leading-relaxed">{model.description}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-green-500/10 p-4 rounded-lg">
                <div className="flex items-center">
                  <TrendingUp className="h-5 w-5 text-green-300 mr-2" />
                  <div>
                    <div className="text-lg font-bold text-green-300">{formatPercentage(model.success_rate)}</div>
                    <div className="text-xs text-green-300">Success Rate</div>
                  </div>
                </div>
              </div>

              <div className="bg-accent-500/10 p-4 rounded-lg">
                <div className="flex items-center">
                  <DollarSign className="h-5 w-5 text-accent-300 mr-2" />
                  <div>
                    <div className="text-lg font-bold text-accent-300">{formatPercentage(model.monthly_return)}</div>
                    <div className="text-xs text-accent-300">Monthly Return</div>
                  </div>
                </div>
              </div>

              <div className="bg-red-500/10 p-4 rounded-lg">
                <div className="flex items-center">
                  <Shield className="h-5 w-5 text-red-300 mr-2" />
                  <div>
                    <div className="text-lg font-bold text-red-300">{model.risk_per_day || 2.0}%</div>
                    <div className="text-xs text-red-300">Risk/Day</div>
                  </div>
                </div>
              </div>

              <div className="bg-green-500/10 p-4 rounded-lg">
                <div className="flex items-center">
                  <Target className="h-5 w-5 text-green-300 mr-2" />
                  <div>
                    <div className="text-lg font-bold text-green-300">{model.target_per_day || 3.0}%</div>
                    <div className="text-xs text-green-300">Target/Day</div>
                  </div>
                </div>
              </div>

              <div className="bg-orange-500/10 p-4 rounded-lg">
                <div className="flex items-center">
                  <Star className="h-5 w-5 text-orange-300 mr-2" />
                  <div>
                    <div className="text-lg font-bold text-orange-300">{formatNumber(model.score)}</div>
                    <div className="text-xs text-orange-300">Score</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white/5 p-4 rounded-lg">
                <h4 className="font-semibold text-slate-100 mb-3">Risk Profile</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Risk Level:</span>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskLevelColor(model.risk_level)}`}
                    >
                      {model.risk_level.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Risk/Day:</span>
                    <span className="font-medium text-red-300">{model.risk_per_day || 2.0}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Target/Day:</span>
                    <span className="font-medium text-green-300">{model.target_per_day || 3.0}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Max Drawdown:</span>
                    <span className="font-medium text-red-300">{formatPercentage(model.max_drawdown)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Sharpe Ratio:</span>
                    <span className="font-medium text-green-300">{model.sharpe_ratio.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 p-4 rounded-lg">
                <h4 className="font-semibold text-slate-100 mb-3">Capital Requirements</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Required Amount:</span>
                    <span className="font-medium text-slate-100">{formatCurrency(model.required_amount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Position Size:</span>
                    <span className="font-medium text-slate-100">Risk-based (0.25% per trade)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Stop Loss:</span>
                    <span className="font-medium text-red-300">ATR-based (1× ATR)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Take Profit:</span>
                    <span className="font-medium text-green-300">2× Risk (2R)</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-accent-500/10 p-4 rounded-lg">
              <h4 className="font-semibold text-slate-100 mb-3">Trading Strategy Overview</h4>
              <div className="text-sm text-slate-300 space-y-2">
                <p>
                  <strong>Risk Management:</strong> {getRiskManagement(model.type)}
                </p>
                <p>
                  <strong>Best Market Conditions:</strong> {getMarketConditions(model.type)}
                </p>
              </div>
            </div>
          </div>

        <div className="flex-shrink-0 bg-white/5 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            onClick={onClose}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-accent-500 text-base font-medium text-white hover:bg-accent-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-ink-900 focus:ring-accent-500 sm:ml-3 sm:w-auto sm:text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ModelInfoModal;
